import React, { useEffect, useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useSnackbar } from 'notistack';
import ProgressDialog from '../../ProgressDialog';
import BatchContext from './BatchContext';
import { Link, Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom';
import BatchCommunication from './BatchCommunication';
import BatchDetails from './BatchDetails';
import Page from '../../Page';
import scheduleTask from '@afosto/utils-react/tasks/scheduleTask';
import { batches as batchesApi } from '@afosto/content/api/Batches';
import { dataset as datasetApi } from '@afosto/content/api/Dataset';
import translations from './translations';

const ViewBatch = () => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [batch, setBatch] = useState(null);
  const [dataset, setDataset] = useState(null);
  const [isFetchingBatch, setIsFetchingBatch] = useState(false);
  const [isValidatingBatch, setIsValidatingBatch] = useState(false);
  const [shouldReloadErrors, setShouldReloadErrors] = useState(false);
  const datasetId = batch?.dataset_id;

  useEffect(() => {
    const fetchBatch = async () => {
      setIsFetchingBatch(true);

      try {
        const response = await batchesApi.getBatch(id);
        const { data = null } = response;

        setBatch(data);
      } catch (err) {
        setBatch(null);
      } finally {
        setIsFetchingBatch(false);
      }
    };

    if (id) {
      fetchBatch().catch(() => {
        // Catch only
      });
    }
  }, [id]);

  useEffect(() => {
    const fetchDataset = async () => {
      try {
        const response = await datasetApi.getDataset(datasetId);
        const { data = null } = response;

        setDataset(data);
      } catch (err) {
        setDataset(null);
      }
    };

    if (datasetId) {
      fetchDataset().catch(() => {
        // Catch only
      });
    }
  }, [datasetId]);

  const handleValidation = async () => {
    try {
      setIsValidatingBatch(true);

      const validationResponse = await batchesApi.validateBatch(batch?.id);
      const taskId = validationResponse.headers?.['x-task-id'];

      await scheduleTask(taskId);

      enqueueSnackbar(intl.formatMessage(translations.feedback.batchValidationSucceeded), {
        variant: 'success',
      });
    } catch (err) {
      if (err?.is_success === false && err?.percentage === 100) {
        setShouldReloadErrors(true);
      }

      enqueueSnackbar(intl.formatMessage(translations.feedback.batchValidationFailed), {
        variant: 'error',
      });
    } finally {
      setIsValidatingBatch(false);
    }
  };

  const handleConfirmation = async () => {
    try {
      await batchesApi.confirmBatch(batch?.id);

      enqueueSnackbar(intl.formatMessage(translations.feedback.batchConfirmed), {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.batchNotConfirmed), {
        variant: 'error',
      });
    }
  };

  const secondaryActions = [
    ...(!batch?.is_accepted
      ? [
          {
            key: 'validate',
            text: intl.formatMessage(translations.secondaryActions.validate),
            action: handleValidation,
          },
        ]
      : []),
    ...(batch?.is_accepted && !batch?.is_confirmed
      ? [
          {
            key: 'confirm',
            text: intl.formatMessage(translations.secondaryActions.confirm),
            action: handleConfirmation,
          },
        ]
      : []),
  ];

  return (
    <BatchContext.Provider
      value={{ batch, dataset, shouldReloadErrors, setShouldReloadErrors, isFetchingBatch }}
    >
      <Page
        title={batch?.reference}
        breadcrumbs={[
          { label: intl.formatMessage(translations.breadcrumbs.batches), path: '/batches' },
          { label: batch?.reference, path: `/batches/${id}`, active: true },
        ]}
        linkComponent={Link}
        subNavigation={{
          current: location.pathname,
          tabs: [
            {
              label: intl.formatMessage(translations.subNavigation.details),
              path: `/batches/${id}`,
            },
            {
              label: intl.formatMessage(translations.subNavigation.communication),
              path: `/batches/${id}/communication`,
            },
          ],
          onChange: (e, value) => history.replace(value),
        }}
        secondaryActions={secondaryActions}
        fullWidth
        isLoading={isFetchingBatch}
      >
        <Switch>
          <Route component={BatchCommunication} path="/batches/:id/communication" />
          <Route component={BatchDetails} path="/batches/:id" />
        </Switch>
      </Page>

      <ProgressDialog
        isOpen={isValidatingBatch}
        message={intl.formatMessage(translations.validateProgressDialog.message)}
      />
    </BatchContext.Provider>
  );
};

export default ViewBatch;
